import { useState, useEffect } from 'react';
import { Button, Spinner } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import Header from '../../components/layout/Header';
import Body from '../../components/layout/Body';
import Tabs from '../../components/layout/Tabs';
import GroupSettings from '../components/GroupSettings';
import FieldSettings from '../components/FieldSettings';
import { useNotice } from '../../contexts/NoticeContext';
import { useURLParams } from '../../contexts/ParamsContext';
import { fetchGroup, saveGroup } from '../../utils/api';

const GroupEdit = () => {
    const [isSaving, setIsSaving] = useState(false);
    const { params, updateUrlParams } = useURLParams();
    const [groupData, setGroupData] = useState({ name: '', fields: [] });
    const { showNotice } = useNotice();

    const handleSave = () => {
        setIsSaving(true);
        saveGroup(groupData)
            .then((savedGroup) => {
                const savedId = savedGroup?.data?.id;
                const fieldIds = savedGroup?.data?.field_ids || [];

                setGroupData(prev => ({
                    ...prev,
                    id: savedId,
                    fields: prev.fields.map((field, index) => ({
                        ...field,
                        id: field.id || fieldIds[index]
                    }))
                }));

                updateUrlParams({ view: 'edit-group', id: savedId });

                setTimeout(() => {
                    showNotice(
                        __(savedGroup?.message || 'Updated successfully.', 'voxycure-framework'),
                        'success'
                    );
                }, 50);
            })
            .catch((error) => {
                showNotice(
                    error?.message || __('Something went wrong while saving group.', 'voxycure-framework'),
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const handleFieldsChange = (updatedFields) => {
        setGroupData({ ...groupData, fields: updatedFields });
    };

    useEffect(() => {
        if (!params.id) return;
        fetchGroup(params.id)
            .then((data) => setGroupData(data))
            .catch((error) => {
                showNotice(
                    error?.message || __('Something went wrong while fetching group.', 'voxycure-framework'),
                    'error'
                );
            });
    }, []);

    const tabList = [
        { name: 'group-settings', title: __('Group Settings', 'voxycure-framework'), className: 'group-settings-tab' },
        { name: 'fields', title: __('Fields', 'voxycure-framework'), className: 'fields-tab' },
    ];

    return (
        <>
            <Header>
                <h2 className='voxycure-editor__header-title'>
                    {groupData.id
                        ? __('Edit Field Group', 'voxycure-framework')
                        : __('Add New Field Group', 'voxycure-framework')}
                </h2>
                <Button
                    className='components-button is-primary voxycure-button'
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <Spinner style={{ verticalAlign: 'middle', marginRight: 6 }} size={16} />
                            {__('Saving...', 'voxycure-framework')}
                        </>
                    ) : (
                        __('Save', 'voxycure-framework')
                    )}
                </Button>
            </Header>

            <Body>
                <Tabs
                    tabs={tabList}
                    renderTab={(tab) => {
                        switch (tab.name) {
                            case 'group-settings':
                                return <GroupSettings groupData={groupData} setGroupData={setGroupData} />;
                            case 'fields':
                                return <FieldSettings fields={groupData.fields || []} onFieldsChange={handleFieldsChange} />;
                            default:
                                return null;
                        }
                    }}
                />
            </Body>
        </>
    );
};

export default GroupEdit;
